/**
 * FASE L2.1 — Global Search Cmd+K modal.
 *
 * Attivazione:
 *   • Cmd/Ctrl + K (event listener globale)
 *   • Bottone icona nell'header admin (opzionale)
 *
 * UX:
 *   • Input debounced 200ms
 *   • Risultati navigabili con ↑↓ + Enter, Esc chiude
 *   • Click/Enter → naviga a /admin/cockpit?highlight={id}
 *
 * Endpoint: GET /api/admin/search?q=X&limit=15
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, X, Loader2, ArrowRight, User, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RAMO_BADGE_CLS = {
  auto: "bg-blue-100 text-blue-800",
  casa: "bg-emerald-100 text-emerald-800",
  vita: "bg-violet-100 text-violet-800",
  salute: "bg-pink-100 text-pink-800",
  rischi_salute: "bg-pink-100 text-pink-800",
  infortuni: "bg-orange-100 text-orange-800",
  viaggio: "bg-cyan-100 text-cyan-800",
  viaggi: "bg-cyan-100 text-cyan-800",
  d_o: "bg-indigo-100 text-indigo-800",
  cyber: "bg-slate-100 text-slate-800",
  fine_arts: "bg-amber-100 text-amber-800",
  rc_professionale: "bg-teal-100 text-teal-800",
};

export default function GlobalSearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const runSearch = useCallback(async (value) => {
    if (!value || value.trim().length < 2) {
      setResults([]); setError(""); setLoading(false); return;
    }
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/admin/search`, {
        params: { q: value.trim(), limit: 15 },
        withCredentials: true,
      });
      setResults(data.results || []);
      setSelectedIdx(0);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore ricerca";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); setError(""); return; }
    // Focus input on open
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, open, runSearch]);

  const doNavigate = useCallback((item) => {
    if (!item?.id) return;
    onClose?.();
    navigate(`/admin/cockpit?highlight=${item.id}`);
  }, [navigate, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (!open) return;
    if (e.key === "Escape") { e.preventDefault(); onClose?.(); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, Math.max(0, results.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(i => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[selectedIdx];
      if (item) doNavigate(item);
    }
  }, [open, results, selectedIdx, onClose, doNavigate]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${selectedIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [selectedIdx]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-20 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="global-search-modal"
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca nome, cognome, email, telefono, targa, CF…"
            className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-slate-400"
            data-testid="global-search-input"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            data-testid="global-search-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div ref={listRef} className="max-h-[400px] overflow-y-auto">
          {q.length >= 2 && !loading && results.length === 0 && !error && (
            <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid="global-search-empty">
              Nessun risultato per &quot;<span className="font-medium">{q}</span>&quot;
            </div>
          )}
          {error && (
            <div className="px-4 py-4 text-sm text-red-700 bg-red-50" data-testid="global-search-error">
              {error}
            </div>
          )}
          {q.length < 2 && (
            <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="global-search-hint">
              Digita almeno 2 caratteri per iniziare la ricerca.
              <div className="mt-2 text-xs">
                <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-700">↑↓</kbd> per navigare
                · <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-700">↵</kbd> per aprire
                · <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-700">Esc</kbd> per chiudere
              </div>
            </div>
          )}
          {results.map((r, i) => {
            const ramoCls = RAMO_BADGE_CLS[r.ramo] || "bg-slate-100 text-slate-700";
            const isSelected = i === selectedIdx;
            return (
              <div
                key={r.id}
                data-idx={i}
                onClick={() => doNavigate(r)}
                onMouseEnter={() => setSelectedIdx(i)}
                className={`px-4 py-3 border-b border-slate-100 cursor-pointer flex items-center gap-3 ${
                  isSelected ? "bg-emerald-50" : "hover:bg-slate-50"
                }`}
                data-testid={`global-search-result-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase ${ramoCls}`}>
                      {r.ramo}
                    </span>
                    {r.partner && (
                      <span className="text-[10px] text-slate-500">· {r.partner}</span>
                    )}
                    {r.stato && (
                      <span className="text-[10px] text-slate-500">· {r.stato}</span>
                    )}
                    <span className="text-[10px] text-slate-400 ml-auto">{r.age_label}</span>
                  </div>
                  <div className="text-sm font-medium text-slate-900 truncate">
                    <User className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                    {r.contatto.nome} {r.contatto.cognome || <span className="text-slate-400">(no nome)</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5 flex items-center gap-3">
                    {r.contatto.email && (
                      <span><Mail className="w-3 h-3 inline mr-0.5" />{r.contatto.email}</span>
                    )}
                    {r.contatto.telefono && (
                      <span><Phone className="w-3 h-3 inline mr-0.5" />{r.contatto.telefono}</span>
                    )}
                    {r.contatto.cf_mask && (
                      <span className="font-mono">{r.contatto.cf_mask}</span>
                    )}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 flex justify-between">
          <div>{results.length > 0 && `${results.length} risultat${results.length === 1 ? "o" : "i"}`}</div>
          <div>
            <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded">⌘K</kbd> per riaprire
          </div>
        </div>
      </div>
    </div>
  );
}
